<script setup lang="ts">
const {
    color = "main",
    loading = false,
    type = "button",
} = defineProps<{
    color?: "main" | "light" | "cancel" | "ghost";
    loading?: boolean;
    type?: "button" | "submit";
}>();
</script>

<template>
    <button
        :type="type"
        class="button"
        :class="[`button_color-${color}`, { button_loading: loading }]"
        :aria-busy="loading"
    >
        <span class="button__loader" />
        <span class="button__content">
            <slot />
        </span>
    </button>
</template>

<style scoped>
.button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 40px;
    margin: 0;
    padding: 12px 20px;
    border: none;
    border-radius: 6px;
    color: #fff;
    font: inherit;
    font-size: var(--custom-font-size);
    font-weight: 700;
    line-height: 1.143;
    letter-spacing: 0.1px;
    cursor: pointer;
    transition:
        color 150ms ease,
        background-color 150ms ease;

    &:disabled {
        cursor: not-allowed;
    }
}

.button_color-main {
    background-color: var(--custom-color-main);

    &:hover {
        background-color: var(--custom-color-main-hover, var(--custom-color-main));
    }

    &:disabled {
        background-color: color-mix(in srgb, var(--custom-color-main), white 50%);
    }
}

.button_color-light {
    background-color: #eef3f9;
    color: var(--custom-color-main);

    &:hover {
        color: var(--custom-color-main-hover, var(--custom-color-main));
    }

    &:disabled {
        color: #bbb4e7;
    }
}

.button_color-cancel {
    background-color: var(--color-error);

    &:hover {
        background-color: #f7344b;
    }

    &:disabled {
        background-color: #f85a6d;
    }
}

/* Secondary action without a background: "Back", "Restart" */
.button_color-ghost {
    padding-right: 0;
    padding-left: 0;
    background: none;
    color: var(--custom-color-gray);
    font-size: calc(var(--custom-font-size) * 0.857);
    font-weight: 400;
    line-height: 1.333;

    &:hover {
        color: #444d55;
    }
}

/* Icon and text are centered against each other, not by the text baseline */
.button__content {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Three bouncing dots instead of the content while loading */
.button__loader,
.button__loader::before,
.button__loader::after {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    opacity: 0;
    animation: button-dots 1.8s infinite ease-in-out both;
}

.button__loader {
    position: absolute;
    top: -18px;
    color: currentColor;
    animation-delay: -0.16s;

    &::before,
    &::after {
        content: "";
        position: absolute;
        top: 0;
    }

    &::before {
        left: -14px;
        animation-delay: -0.32s;
    }

    &::after {
        left: 14px;
    }
}

.button_loading {
    .button__loader,
    .button__loader::before,
    .button__loader::after {
        opacity: 1;
    }

    .button__content {
        opacity: 0;
    }
}

@keyframes button-dots {
    0%,
    80%,
    100% {
        box-shadow: 0 2.5em 0 -1.3em;
    }

    40% {
        box-shadow: 0 2.5em 0 0;
    }
}
</style>
